/**
 * Maps detected sky conditions onto a subject's own lighting options.
 *
 * The sky says what's happening outdoors; each subject phrases light in its
 * own vocabulary. Outdoor subjects take the option nearest the sky's
 * brightness. Indoor-leaning subjects (food, parties) only take a day/night
 * hint — a clear sky says nothing about the lamps in a restaurant.
 */

import { lightingFor } from './lighting'
import type { Sky } from './solar'

/** Subjects whose light is mostly indoors, where the sky is only a hint. */
const INDOOR_SUBJECTS: Record<string, { day: string; night: string }> = {
  food: { day: 'window', night: 'restaurant' },
  indoors: { day: 'daylight-room', night: 'evening-room' },
}

export interface LightSuggestion {
  optionId: string
  optionLabel: string
  /** Why this option, e.g. "clear and bright near you". */
  reason: string
  /** Hedged for indoor subjects, where the sky is only a hint. */
  confident: boolean
}

export function suggestLight(subjectId: string, sky: Sky): LightSuggestion {
  const lighting = lightingFor(subjectId)

  const indoor = INDOOR_SUBJECTS[subjectId]
  if (indoor) {
    const targetId = sky.isDaylight ? indoor.day : indoor.night
    const option =
      lighting.options.find((o) => o.id === targetId) ??
      lighting.options.find((o) => o.id === lighting.defaultId)!
    return {
      optionId: option.id,
      optionLabel: option.label,
      reason: sky.isDaylight
        ? `daylight outside — if you're indoors, "${option.label}" is the likely light`
        : `${sky.blurb} outside — indoors that usually means "${option.label}"`,
      confident: false,
    }
  }

  // Outdoor subject: nearest option in stops to the sky's brightness.
  const option = lighting.options.reduce((best, o) =>
    Math.abs(Math.log2(o.ev / sky.ev)) < Math.abs(Math.log2(best.ev / sky.ev)) ? o : best,
  )
  return {
    optionId: option.id,
    optionLabel: option.label,
    reason: `${sky.blurb} near you`,
    confident: true,
  }
}
